/**
 * Page-lock manifest.
 *
 * One entry per page that should display the under-construction lock overlay.
 * A page is locked iff it appears in this map. Removing the entry unlocks the
 * page; no page-component changes are needed.
 *
 * Keys are the `pageId` strings passed to `<LazyRoute pageId="…" />` in
 * `routes.tsx`. By convention, keys match the feature module folder name
 * under `src/features/`.
 */

import type { PageLock, WorkStage } from './types'

/**
 * Work stages shared by the two remaining locked Insights pages.
 *
 * They describe the Insights pipeline as a whole rather than any one page,
 * because that is what the operator actually needs to know: which parts are
 * live (and therefore which pages work) and which are not.
 *
 * Numbers here are load-bearing and were verified against the live warehouse
 * and the deployed service — do not round them up from the design doc, which
 * describes an end state rather than what shipped.
 */
const INSIGHTS_STAGES: WorkStage[] = [
  {
    label: 'Reports Catalog with parameterised SQL',
    status: 'complete',
    note: 'Semantic spec for every chart',
  },
  {
    label: 'Six-page UI scaffold',
    status: 'complete',
    note: 'Compiles clean, lazy-loaded, sidebar entry, per-page locks',
  },
  {
    label: 'Insights service scaffold',
    status: 'complete',
    note: 'Go service + OpenAPI + handler stubs; 30 gateway-proxied routes',
  },
  {
    label: 'Architecture locked',
    status: 'complete',
    note: 'Producer/reader split, ADR-25 revision 2',
  },
  {
    label: 'Schema design + freshness contracts (v1 tables)',
    status: 'complete',
    note: 'report_asc_traffic_daily, report_revenue_daily, report_cohort_retention, report_active_subs_daily, report_churn_daily, report_dau_daily and report_arr_daily are live in mac-worker-analytics.kodeon_insights. Staleness is per-table on _published_at: 30h for the six daily tables, 11 days for the weekly-published retention table. report_arr_daily is registered DAILY despite its figure being reported weekly — the trigger is the publish cadence (10:35 ET daily), not the report cadence and not the row grain',
  },
  {
    label: 'mac-worker-analytics publish tasks (v1 tables)',
    status: 'complete',
    note: 'All five v1 tables are populated: ~1.88M traffic rows and ~42.5K revenue rows covering 19 apps (17 AppLife + Goals + Breethe) plus the Unmapped sentinel, 2,459 daily retention cohorts covering 17 apps, and ~268K subscription-state plus ~538K churn rows from Apple ASC',
  },
  {
    label: 'services/insights/ reader implementation',
    status: 'complete',
    note: 'Serves /overview, /acquisition/chart, /acquisition/facets, /engagement/dau, /engagement/retention, /monetisation/active-subscribers, /monetisation/churn, /monetisation/trial-conversion, /monetisation/breethe-funnel and /monetisation/arr (D14); the remaining routes are 501 until their tables exist. /monetisation/breethe-funnel serves TWO VIEWS under the ONE report id D13 since 2026-09-21 (task 4101): the trial-anchored funnel from report_breethe_funnel_daily, and the paywall-anchored funnel from report_breethe_paywall_funnel_daily, which is PUBLISHED AND SERVED rather than pending. The two are selected, never merged — their cohorts are defined by different events. D13 deliberately did NOT acquire a second report id: ReportMetadataBadge renders the id on every chart, so a second one would present a second cut of one funnel as an unrelated report',
  },
  {
    label: 'Frontend wired to live endpoints',
    status: 'in-progress',
    note: 'Overview, Acquisition, Engagement and Monetisation are live and unlocked. This page is still mock data — it needs the customer-keyed tables, which have not been published',
  },
  {
    label: 'Remaining contract tables (feature lift, customer-keyed)',
    status: 'pending',
    note: 'report_feature_lift_weekly and the AD-HOC customer-keyed tier. report_paywall_conversion_daily LEFT this list on 2026-09-17 (task 4098c) and is NOT pending — it is not buildable as specified. ADR-25 gives it a date x app x paywall_event grain that its production source cannot produce (13_firebase_paywall_variants_daily.sql ends GROUP BY origin, with no day column), and there is no portfolio-wide paywall event to count. Verifiable in .agents/skills/firebase-analytics-kodeon-backend/references/apps/: HealthView emits paywall_shown, Do Habits and WeFast subscription_upgrade_modal_opened, TimeBloc and Days none. A 2026-09-16 audit additionally found WeBurn on page_view_paywall and MoS Android emitting none; neither app is indexed under that skill, so those two are attributed to the audit rather than checkable here (review round 1, PR #168). Unlocking D11 is mobile instrumentation work across ~19 apps, not a publish task. report_dau_daily LEFT this list on 2026-09-16 — it is published and serves both the Engagement panel (C1) and the Overview DAU tile. report_arr_daily LEFT it on 2026-09-17 (task 4098b) — it is published and serves the Overview ARR tile and the Monetisation ARR panel (D14). report_breethe_paywall_funnel_daily was NEVER on this list and is not pending — it was published by task 4100 on 2026-09-21 and is SERVED as the second view of D13 by task 4101. It does NOT unlock D11: Breethe having a good paywall event does not create a portfolio-wide one, so the 4098c reason string stands unchanged. A churn rate is NOT a pending table — it is not derivable from Apple ASC churn reporting, which publishes counts with no base population. MRR is not pending either, and for a different reason again: it is CANCELLED, replaced by ARR',
  },
  {
    label: 'Freshness monitoring + alerting',
    status: 'pending',
    note: 'The stale-source chip and caveats are rendered per-report today; proactive alerting is not built',
  },
]

export const PAGE_LOCKS: Record<string, PageLock> = {
  'agent-cost': {
    title: 'Agent Cost Tracking',
    subtitle: 'Token usage and spend across agent sessions',
    description:
      'Per-session token counts are flowing in, but none of the connections from those tokens through to actual spend have been verified yet. Locked until we can trust the dollar figures end to end.',
    stages: [
      { label: 'Per-session token reporting', status: 'complete' },
      {
        label: 'Token → spend connectivity verification',
        status: 'in-progress',
        note: 'Tracing tokens through provider pricing to a trusted spend figure',
      },
      { label: 'Cost roll-ups by agent and customer', status: 'pending' },
      { label: 'Budget alerts and forecasts', status: 'pending' },
    ],
  },
  'integrations': {
    title: 'Integrations',
    subtitle: 'Partner integration health across the Kodeon platform',
    description:
      'Health and status for every external partner integration the platform depends on. Locked while we finalise the health-checking methodology so the indicators reported here are trustworthy.',
    stages: [
      { label: 'Integration inventory', status: 'complete' },
      {
        label: 'Health-check methodology',
        status: 'in-progress',
        note: 'Defining what "healthy" means per partner',
      },
      { label: 'Per-partner probes', status: 'pending' },
      { label: 'Status surfacing & alerting', status: 'pending' },
    ],
  },
  'catalog': {
    title: 'Catalog',
    subtitle: 'Apps, bundles, and suites across the Kodeon platform',
    description:
      'Browse and manage the platform catalog of apps, bundles, and suites with their pricing tiers and AppLife in-app purchase mappings. Locked while we investigate why the AppLife IAPs column reports duplicate identifiers for some apps.',
    stages: [
      { label: 'Catalog list with apps, bundles, and suites tabs', status: 'complete' },
      {
        label: 'AppLife IAPs column de-duplication',
        status: 'in-progress',
        note: 'Tracing why the same IAP identifier appears twice for some apps — likely a join fan-out or stale mapping in the catalog source',
      },
    ],
  },
  'email-templates': {
    title: 'Email Templates',
    subtitle: 'Transactional email templates and previews',
    description:
      'Author, preview, and manage the transactional emails the platform sends to customers. Locked while we resolve a rendering gap where the in-UI preview does not match the email as actually delivered.',
    stages: [
      { label: 'Template list and editor', status: 'complete' },
      {
        label: 'Preview rendering parity with delivered email',
        status: 'in-progress',
        note: 'Suspected cause: the in-UI renderer is not applying all of the theme overrides used by the delivery pipeline, so the preview diverges from what recipients see',
      },
      { label: 'Side-by-side preview vs. delivered comparison', status: 'pending' },
    ],
  },
  // ── Insights ───────────────────────────────────────────────────────────
  //
  // Insights is locked PER PAGE, not as a feature. Overview, Acquisition,
  // Engagement and Monetisation are LIVE against the six published contract
  // tables and carry no entry here — they are released. The two pages below
  // are still scaffolds rendering mock data and stay locked. That granularity
  // comes from `routes.tsx` giving each page its own `pageId` (ADR-25 §7.2);
  // there is no per-page machinery beyond these keys.
  //
  // Engagement and Monetisation are both only PARTIALLY live, and were
  // unlocked anyway. Engagement serves C4 (retention) and C1 (DAU, published
  // 2026-09-16) while C5 has no contract table; Monetisation serves D8
  // (subscriber base) and D5/D6 (churn counts) while its other six panels have
  // none. In both cases a whole-page lock would have hidden working panels to
  // protect the ones that do not exist, so each page renders an explicit "no
  // data source" panel per missing report instead — the finer-grained and more
  // truthful instrument.
  //
  // Overview composes SIX of those tables into the Report A KPI strip. As of
  // 2026-09-17 it carries DAU, active subscribers and churn (task 4098a) and
  // ARR (task 4098b) as real tiles; only paywall conversion remains "Not
  // available in v1" there.
  //
  // ── MRR left the Overview unavailable list WITHOUT gaining a tile ────────
  //
  // That is normally the shape of a regression (PR #161 deleted the DAU tile
  // exactly that way), and it is correct here for the one reason it can be:
  // MRR is CANCELLED, not unimplemented. ARR replaced it, and the weekly
  // report had already dropped MRR in F50 as "not meaningful; ARR is the
  // canonical surface". A locked MRR tile beside the ARR tile that replaced it
  // would advertise a monthly figure twelve times smaller that nobody intends
  // to build.
  //
  // ── ARR is the ONE tile on the strip that is not Apple ASC ───────────────
  //
  // report_arr_daily is subscription-system data with MIXED PER-APP
  // provenance: 29 apps via RevenueCat, breethe_ios via its own Aurora billing
  // backend (StoreKit-2 direct, zero RevenueCat rows by design, ~79% of
  // portfolio ARR) and two apps with no source at all publishing NULL. It will
  // not reconcile with the proceeds tiles and must never be summed with them,
  // and it must not be labelled "RevenueCat" — that claim is false about the
  // largest contributor.
  //
  // Monetisation additionally declares two MEASURES unavailable rather than
  // fabricating them: MRR (D9), and the overall churn rate (D4), because
  // report_churn_daily publishes no base population and the active-subscriber
  // count is not a valid denominator for it. The churn-rate absence is
  // MEASURE-level, not table-level: the table is published and read. MRR's is
  // now a SCOPE decision rather than a data gap.
  //
  // The shared stage list describes where the pipeline as a whole stands, so
  // an operator who hits either locked page understands why the live pages
  // work and that one does not.
  'insights-explorer': {
    title: 'Insights — Customer Explorer',
    subtitle: 'Per-customer timeline, purchase ledger and support history',
    description:
      'Search a customer and read their whole history in one place: signup, sessions, purchases, refunds, comms and support. Locked because it needs the customer-keyed AD-HOC tier, which has no published tables yet. The two live Insights pages are aggregate-only and hold no customer-level data.',
    stages: INSIGHTS_STAGES,
  },
  'insights-cohort-builder': {
    title: 'Insights — Cohort Builder',
    subtitle: 'Define a cohort, compute any metric over it',
    description:
      'Build an arbitrary cohort from filters and compute any of the F-series metrics over it, with an optional baseline for side-by-side comparison. Locked because it composes on top of the customer-keyed tables the Customer Explorer needs, none of which are published. The UI is a scaffold and its results are placeholders.',
    stages: INSIGHTS_STAGES,
  },

}

/**
 * Type guard for whether a page is in the lock manifest.
 */
export function hasLockEntry(pageId: string): boolean {
  return Object.prototype.hasOwnProperty.call(PAGE_LOCKS, pageId)
}
